import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';

import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { companyActions } from '@nxsys/store/slices/company/reducer/companySlice';
import { GetExpenseCompanyListParams } from '@nxsys/store/slices/company/types';
import { VATCode } from '@nxsys/store/slices/paymentSetting/types';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { IExpenseCompany } from '@nxsys/types/models/expense';
import { getVATCodeOption } from '@nxsys/utils';
import { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import CreateExpenseModal from './components/CreateExpenseModal';
import { ExpenseValues } from './components/ExpenseForm';
import {
  useCreateExpenseCompany,
  useDeleteExpenseCompany,
  useUpdateExpenseCompany,
} from '@nxsys/hooks/api/company';
import {
  CreateExpenseCompanyParams,
  UpdateExpenseCompanyParams,
} from '@nxsys/types/api/company';
import { showMessage } from '@nxsys/utils/message';
import EditExpenseModal from './components/EditExpenseModal';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';


type Props = {
  data?: ICompany;
};

const ExpenseTab: React.FC<Props> = ({ data }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();

  const { expenseCompanyList } = useSelector(
    (state: AppState) => state.companyStore,
  );
  const { loading, list, pagination, queryParams } = expenseCompanyList;

  useEffect(() => {
    if (data?.id) {
      handleFetchData({
        page: 0,
        size: 20,
      });
    }
  }, [data?.id]);

  const handleFetchData = (params: GetExpenseCompanyListParams) => {
    const newParams = { ...params, id: data?.id || '' };
    dispatch(companyActions.getExpenseCompanyList(newParams));
  };

  const refetch = () =>
    handleFetchData({
      ...queryParams,
    });

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const columns: ColumnsType<IExpenseCompany> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => toggleEditModal(record)}>
            <Icons.IconEdit />
          </li>
          <li onClick={() => handleDeleteExpense(record.id)}>
            <Icons.IconDelete />
          </li>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'expenseCode',
      title: messages['common.expenseCode'],
      dataIndex: 'expenseCode',
    },
    {
      key: 'expenseName',
      title: messages['common.expenseName'],
      dataIndex: 'expenseName',
    },
    {
      key: 'vatCodeId',
      title: messages['common.vatCode'],
      dataIndex: 'vatCode',
      render: (value: VATCode) => getVATCodeOption(value)?.label || '-',
    },
  ];

  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);

  const { run: createExpense } = useCreateExpenseCompany();
  const handleCreateExpense = async (values: ExpenseValues) => {
    const params: CreateExpenseCompanyParams = {
      expenseCode: values.expenseCode,
      expenseName: values.expenseName,
      vatCodeId: values.vatCodeId?.value || null,
    };
    const res = await createExpense(data?.id ?? '', params);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.addSuccess'],
      });
      refetch();
      setIsOpenCreateModal(false);
    }
  };

  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [selectedData, setSelectedData] = useState<IExpenseCompany | null>(
    null,
  );
  const toggleEditModal = (data?: IExpenseCompany) => {
    setSelectedData(data || null);
    setIsOpenEditModal(true);
  };
  const { run: updateExpense } = useUpdateExpenseCompany();
  const handleEditExpense = async (values: ExpenseValues) => {
    const params: UpdateExpenseCompanyParams = {
      expenseCode: values.expenseCode,
      expenseName: values.expenseName,
      vatCodeId: values.vatCodeId?.value || null,
    };
    const res = await updateExpense(
      data?.id ?? '',
      selectedData?.id ?? '',
      params,
    );
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
      refetch();
      setIsOpenEditModal(false);
    }
  };

  const { run: deleteExpense } = useDeleteExpenseCompany();
  const handleDeleteExpense = async (id: string) => {
    AppConfirmationDelete.open({
      title: messages['common.delete'],
      paragraph: messages['common.deleteConfirm'],
      onConfirm: async () => {
        const res = await deleteExpense(data?.id ?? '', id);
        if (res.success) {
          showMessage({
            type: 'success',
            content: messages['common.deleteSuccess'],
          });
          refetch();
        }
      },
    });
  };

  return (
    <>
      <div className="p-[16px]">
        <div className="flex justify-between items-center mb-[16px]">
          <AppInputSearch onChange={handleSearchChange} />
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            onClick={() => setIsOpenCreateModal(true)}
          />
        </div>
        <AppsContent>
          <AppTableContainer
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 800, y: 700 }}
            titleEmpty={messages['common.noData']}
            columns={columns}
            onChangePagination={onChangePagination}
          />
        </AppsContent>
      </div>
      <CreateExpenseModal
        isOpen={isOpenCreateModal}
        toggle={() => setIsOpenCreateModal(false)}
        onSubmit={handleCreateExpense}
      />
      {isOpenEditModal && selectedData && (
        <EditExpenseModal
          isOpen={isOpenEditModal}
          toggle={() => setIsOpenEditModal(false)}
          data={selectedData}
          onSubmit={handleEditExpense}
        />
      )}
    </>
  );
};

export default ExpenseTab;
